import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import HomePage from './pages/HomePage';
import PublicBookingPage from './pages/PublicBookingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import { auth } from './services/firebase';
import {
  initPushNotifications,
  enablePushNotifications,
  disablePushNotifications,
  isPushEnabled,
} from './services/pushNotifications';
import './index.css';

function AuthPortal({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('app-theme') || 'light');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Once signed in, prime push notifications (SW + foreground handler + token).
  useEffect(() => {
    let cancelled = false;
    if (user) {
      (async () => {
        await initPushNotifications();
        if (!cancelled) setPushEnabled(await isPushEnabled());
      })();
    } else {
      setPushEnabled(false);
    }
    return () => {
      cancelled = true;
    };
  }, [user]);

  const handleSignIn = async (event) => {
    event.preventDefault();
    setError('');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setPassword('');
    } catch (signInError) {
      setError(signInError?.message || 'Failed to sign in');
      setPassword('');
    }
  };

  const handleSignOut = async () => {
    await disablePushNotifications();
    setPushEnabled(false);
    await signOut(auth);
  };

  const handlePushToggle = async () => {
    if (pushEnabled) {
      await disablePushNotifications();
      setPushEnabled(false);
      toast('Push notifications disabled');
    } else {
      const ok = await enablePushNotifications();
      if (ok) {
        setPushEnabled(true);
        toast('Push notifications enabled');
      }
    }
  };

  if (loading) {
    return <div className="global-auth-loading">Checking authentication...</div>;
  }

  if (!user) {
    return (
      <div className="global-auth-portal">
        <div className="global-auth-card">
          <h2>Admin Portal</h2>
          <p>Sign in with Firebase Authentication to access this site.</p>
          <form onSubmit={handleSignIn}>
            <label htmlFor="auth-email">Email</label>
            <input
              type="email"
              id="auth-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@business.com"
              required
              autoFocus
            />
            <label htmlFor="auth-password">Password</label>
            <input
              type="password"
              id="auth-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
            {error ? <p className="global-auth-error">{error}</p> : null}
            <button type="submit">Sign In</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="global-auth-bar">
        <button
          type="button"
          className="theme-icon-toggle"
          onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
          aria-label={theme === 'light' ? 'Enable dark mode' : 'Enable light mode'}
          title={theme === 'light' ? 'Dark mode' : 'Light mode'}
        >
          {theme === 'light' ? 'Dark' : 'Light'}
        </button>
        <span className="auth-user-email">{user.email || 'Signed in'}</span>
        <button
          type="button"
          className="push-toggle"
          onClick={handlePushToggle}
          aria-label={pushEnabled ? 'Disable push notifications' : 'Enable push notifications'}
          title={pushEnabled ? 'Push notifications on (click to turn off)' : 'Enable push notifications for new appointments'}
        >
          {pushEnabled ? '🔔 On' : '🔕 Off'}
        </button>
        <button type="button" onClick={handleSignOut}>Logout</button>
      </div>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3500,
          style: { borderRadius: '12px', padding: '12px 16px', fontSize: '0.9rem' },
        }}
      />
      {children}
    </>
  );
}

// Register service worker in production only
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js')
      .catch((err) => console.error('Service worker registration failed:', err));
  });
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        {/* Public routes — no login required */}
        <Route path="/book" element={<PublicBookingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/ToS" element={<TermsOfServicePage />} />
        {/* Authenticated app */}
        <Route path="/" element={<AuthPortal><HomePage /></AuthPortal>} />
        <Route path="*" element={<AuthPortal><HomePage /></AuthPortal>} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>
);
